import { Directive, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FeatureFlags } from '../../../interfaces/feature-flags.interface';
import { FeatureFlagsService } from '../../../services/feature-flags/feature-flags.service';

/**
 * Diretiva estrutural para exibir/ocultar elementos baseado em Feature Flags
 * 
 * Uso nos templates:
 * 
 * ```html
 * <!-- Exibir apenas se a flag estiver habilitada -->
 * <div *appFeatureFlag="'newCheckoutFeatureEnabled'">
 *   Novo checkout aqui
 * </div>
 * 
 * <!-- Usando com else -->
 * <div *appFeatureFlag="'newCheckoutFeatureEnabled'; else oldCheckout">
 *   Novo checkout
 * </div>
 * <ng-template #oldCheckout>
 *   Checkout antigo
 * </ng-template>
 * ```
 */
@Directive({
  selector: '[appFeatureFlag]'
})
export class FeatureFlagDirective implements OnInit, OnDestroy {
  @Input('appFeatureFlag') flagName: keyof FeatureFlags;
  @Input('appFeatureFlagElse') elseTemplate: TemplateRef<any> | null = null;

  private destroy$ = new Subject<void>();
  private hasView = false;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private featureFlagsService: FeatureFlagsService
  ) {}

  ngOnInit(): void {
    if (!this.flagName) {
      console.error('[FeatureFlagDirective] Nome da flag não fornecido');
      return;
    }

    // Observa mudanças nas flags
    this.featureFlagsService.getFlags()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateView();
      });

    // Atualização inicial
    this.updateView();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateView(): void {
    const isEnabled = this.featureFlagsService.isEnabled(this.flagName);

    if (isEnabled) {
      this.showMainTemplate();
    } else {
      this.showElseTemplate();
    }
  }

  private showMainTemplate(): void {
    if (!this.hasView) {
      this.viewContainer.clear();
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    }
  }

  private showElseTemplate(): void {
    this.viewContainer.clear();
    this.hasView = false;

    if (this.elseTemplate) {
      this.viewContainer.createEmbeddedView(this.elseTemplate);
    }
  }
}
